import React, { useMemo } from 'react';
import {
  Pagination,
  SxProps,
  Theme,
  Box,
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';

interface CustomPaginationProps {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
}

const paginationSx: SxProps<Theme> = {
  '& .MuiPaginationItem-root': {
    width: 42,
    height: 38,
    fontSize: '0.875rem',
    fontWeight: 500,
    borderRadius: 2,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  },
  '& .MuiPaginationItem-root.Mui-selected': {
    backgroundColor: 'primary.main',
    color: '#fff',
    '&:hover': {
      backgroundColor: 'primary.dark',
    },
  },
};

const containerSx: SxProps<Theme> = {
  py: 2,
  px: { xs: 1, sm: 2 },
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  rowGap: 1,
  columnGap: 2,
};

const getEntryRangeText = (
  page: number,
  pageSize: number,
  totalCount: number
): string => {
  if (totalCount === 0) return 'No entries to show';
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, totalCount);
  return `Showing ${start} to ${end} of ${totalCount} entries`;
};

const CustomPagination: React.FC<CustomPaginationProps> = ({
  page,
  pageSize,
  totalCount,
  onPageChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const totalPages = useMemo(
    () => Math.ceil(totalCount / pageSize),
    [totalCount, pageSize]
  );

  const entryRangeText = useMemo(
    () => getEntryRangeText(page, pageSize, totalCount),
    [page, pageSize, totalCount]
  );

  return (
    <Box component="nav" sx={containerSx} aria-label="Pagination Navigation">
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ flexGrow: 1 }}
        aria-live="polite"
        role="status"
      >
        {entryRangeText}
      </Typography>

      {totalPages > 0 && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={(event, newPage) => onPageChange(event, newPage - 1)}
            siblingCount={isMobile ? 0 : 1}
            boundaryCount={1}
            shape="rounded"
            showFirstButton={!isMobile}
            showLastButton={!isMobile}
            sx={paginationSx}
          />
        </Stack>
      )}
    </Box>
  );
};

export default CustomPagination;
